import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/utils/movies';

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(0);

  const go = useCallback(
    (next: number) => {
      setIndex((next + movies.length) % movies.length);
    },
    [movies.length],
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((value) => (value + 1) % movies.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [movies.length]);

  const onTouchStart = (event: React.TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const onTouchEnd = (event: React.TouchEvent) => {
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (delta > 50) go(index - 1);
    if (delta < -50) go(index + 1);
  };

  if (movies.length === 0) return null;

  return (
    <section className="relative w-full overflow-hidden rounded-lg bg-primary-950" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <div className="relative w-full h-[420px] md:h-[560px]">
        {movies.map((movie, i) => (
          <div
            key={movie.id}
            className={`absolute inset-0 transition-opacity duration-700 ${i === index ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          >
            <img
              src={movie.backdrop}
              alt={`${movie.title} 焦点影片海报`}
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/70 to-primary-950/20"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-transparent"></div>
            <div className="relative h-full w-full flex items-center">
              <div className="w-full px-5 md:px-10 max-w-2xl">
                <div className="flex items-center gap-2 mb-4">
                  <span className="px-2.5 py-1 rounded bg-accent-500 text-primary-950 text-xs font-label font-semibold">
                    焦点推荐
                  </span>
                  <span className="px-2.5 py-1 rounded bg-background-50/15 text-background-100 text-xs font-label">
                    {movie.genres.join(' / ')}
                  </span>
                </div>
                <h2 className="font-heading font-extrabold text-3xl md:text-5xl text-background-50 leading-tight">
                  {movie.title}
                </h2>
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-background-200">
                  <span className="flex items-center gap-1 text-accent-400 font-heading font-bold">
                    <i className="ri-star-fill w-4 h-4 flex items-center justify-center"></i>
                    {movie.rating.toFixed(1)}
                  </span>
                  <span>{movie.year}</span>
                  <span>{movie.region}</span>
                  <span>{movie.status}</span>
                </div>
                <p className="mt-4 text-sm md:text-base text-background-200/90 leading-relaxed line-clamp-3 hidden sm:block">
                  {movie.synopsis}
                </p>
                <div className="mt-6 flex items-center gap-3">
                  <Link
                    to={`/detail/${movie.id}`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 font-label font-semibold text-sm hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-play-fill w-5 h-5 flex items-center justify-center"></i>
                    在线观看
                  </Link>
                  <Link
                    to={`/detail/${movie.id}`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-50/40 text-background-50 font-label text-sm hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-information-line w-5 h-5 flex items-center justify-center"></i>
                    影片详情
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={() => go(index - 1)}
          aria-label="上一张"
          className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 rounded-full bg-primary-950/50 text-background-50 hover:bg-accent-500 hover:text-primary-950 transition-colors flex items-center justify-center cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl w-5 h-5 flex items-center justify-center"></i>
        </button>
        <button
          type="button"
          onClick={() => go(index + 1)}
          aria-label="下一张"
          className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-9 h-9 md:w-10 md:h-10 rounded-full bg-primary-950/50 text-background-50 hover:bg-accent-500 hover:text-primary-950 transition-colors flex items-center justify-center cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-xl w-5 h-5 flex items-center justify-center"></i>
        </button>

        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2">
          {movies.map((movie, i) => (
            <button
              key={movie.id}
              type="button"
              onClick={() => go(i)}
              aria-label={`切换到第 ${i + 1} 张`}
              className={`h-1.5 rounded-full transition-all cursor-pointer ${i === index ? 'w-6 bg-accent-500' : 'w-1.5 bg-background-50/50'}`}
            ></button>
          ))}
        </div>
      </div>
    </section>
  );
}